/**
 * One counting rule for review areas, everywhere (2026-10-14, Part D of the row rule): a count of review areas counts
 * every review area that carries questions, sub-areas included; a parent with sub-areas is a heading over them, not a
 * row of its own. The label says how many of the count are sub-areas, so "13 review areas (4 of them sub-areas)" is
 * the same number on the library, the Structure screen, the workstream picker and the engagement.
 */
export const reviewAreaWords = (total: number, subAreas: number): string =>
  `${total} review area${total === 1 ? "" : "s"}${subAreas ? ` (${subAreas} of them sub-areas)` : ""}`;

/**
 * Decision 108: a count names the tree it counts when both the library's and the client's version of the same row
 * exist. A workstream on the client's adopted copy says "of your Technology (adopted from the library)"; one on the
 * library's own row says nothing, because there is only one tree to count.
 */
type Tree = { origin: "library" | "own"; adoptedFromLibrary: boolean; reviewAreas?: number | null } | null | undefined;
export const treeWords = (w: { label: string; tree?: Tree }): string =>
  w.tree?.origin === "own" ? `, of your ${w.label}${w.tree.adoptedFromLibrary ? " (adopted from the library)" : ""}` : "";
/** The same clause mid-sentence, closed with a comma: "none of the 9 review areas in scope, of your Technology, is rated". */
export const treeWordsMid = (w: { label: string; tree?: Tree }): string => { const t = treeWords(w); return t ? `${t},` : ""; };
/**
 * Wherever review areas in scope are counted, the denominator is stated: "10 of 13 review areas in scope". The total is
 * the tree the workstream runs on; without it (an older response) the areas the workstream knows of stand in.
 */
export const scopeWords = (w: { label: string; tree?: Tree; coverage: { inScope: number; outOfScope: number } }, mid = false): string => {
  const total = w.tree?.reviewAreas ?? (w.coverage.inScope + w.coverage.outOfScope);
  return `${w.coverage.inScope} of ${total} review area${total === 1 ? "" : "s"} in scope${mid ? treeWordsMid(w) : treeWords(w)}`;
};
